// src/pages/analytics/CompetitiveAnalytics.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Typography, Card, Chip, useTheme, alpha,
  Breadcrumbs, Link, Tab, Tabs, CircularProgress, Button, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CompareArrows as CompareIcon,
  Share as ShareIcon,
  Public as GlobeIcon,
  Groups as AudienceIcon,
  ThumbUp as EngagementIcon,
  NavigateNext as NavigateNextIcon,
  Star as StarIcon,
  Equalizer as MetricsIcon,
  Timeline as TrendIcon,
} from '@mui/icons-material';
import { 
  ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip as RechartsTooltip, Legend, PieChart, Pie, Cell, AreaChart, Area,
  ComposedChart, Scatter, ScatterChart, ZAxis, Radar, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis
} from 'recharts';

// Import custom components
import KPICard from '../../components/features/analytics/KPICard';
import ChartCard from '../../components/features/analytics/ChartCard';
import DateRangePicker from '../../components/ui/DateRangePicker';
import ExportButton from '../../components/features/analytics/ExportButton';
import AnalyticsFilters from '../../components/features/analytics/AnalyticsFilters';
import MetricComparison from '../../components/features/analytics/MetricComparison';

// Import store
import useAnalyticsStore from '../../stores/analyticsStore';
import useNotificationStore from '../../stores/notificationStore';

// Import services
import { exportAnalyticsData } from '../../services/analyticsService';

// Import utils
import { formatNumber } from '../../utils/analyticsUtils';

// Import responsive styles
import { 
  getKpiGridProps, 
  getChartGridProps, 
  getPageHeaderStyles,
  getTabsContainerStyles,
} from '../../styles/analyticsResponsive';

/**
 * Competitive Analytics Page Component
 */
const CompetitiveAnalytics = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  
  // Define filter state
  const [filters, setFilters] = useState({
    competitorId: 'all',
    platform: [],
    contentType: 'all',
    metric: 'all',
    region: 'all',
  });
  
  const { 
    competitiveAnalytics, isLoading, criticalError, fetchCompetitiveAnalytics,
    dateRange, setDateRange, clearError,
  } = useAnalyticsStore();
  
  const { showError } = useNotificationStore();
  
  // Define filter options
  const filterOptions = [
    {
      key: 'competitorId',
      label: 'Competitor',
      type: 'select',
      options: [
        { value: 'all', label: 'All Competitors' },
        { value: 'competitor1', label: 'Global Media Group' },
        { value: 'competitor2', label: 'Digital Solutions Inc' },
        { value: 'competitor3', label: 'Creative Agency Co' },
        { value: 'competitor4', label: 'Modern Marketing Ltd' },
      ],
      defaultValue: 'all',
      helperText: 'Filter by specific competitor',
    },
    {
      key: 'platform',
      label: 'Platform',
      type: 'multi-select',
      options: [
        { value: 'facebook', label: 'Facebook' },
        { value: 'instagram', label: 'Instagram' },
        { value: 'twitter', label: 'Twitter' },
        { value: 'linkedin', label: 'LinkedIn' },
        { value: 'tiktok', label: 'TikTok' },
      ],
      defaultValue: [],
      helperText: 'Filter by social platform',
    },
    {
      key: 'contentType',
      label: 'Content Type',
      type: 'select',
      options: [
        { value: 'all', label: 'All Types' },
        { value: 'post', label: 'Posts' },
        { value: 'video', label: 'Videos' },
        { value: 'reels', label: 'Reels' },
        { value: 'story', label: 'Stories' },
      ],
      defaultValue: 'all',
    },
    {
      key: 'metric',
      label: 'Metric',
      type: 'select',
      options: [
        { value: 'all', label: 'All Metrics' },
        { value: 'followers', label: 'Followers' },
        { value: 'engagement', label: 'Engagement' },
        { value: 'reach', label: 'Reach' },
        { value: 'mentions', label: 'Mentions' },
      ],
      defaultValue: 'all',
    },
  ];
  
  // Fetch data when filters change
  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchCompetitiveAnalytics({
          competitorId: filters.competitorId !== 'all' ? filters.competitorId : undefined,
          platform: filters.platform.length > 0 ? filters.platform.join(',') : undefined,
          contentType: filters.contentType !== 'all' ? filters.contentType : undefined,
          metric: filters.metric !== 'all' ? filters.metric : undefined,
          region: filters.region !== 'all' ? filters.region : undefined,
          startDate: dateRange.startDate.toISOString().split('T')[0],
          endDate: dateRange.endDate.toISOString().split('T')[0],
        });
      } catch (error) {
        showError(`Failed to load competitive analytics: ${error.message || 'Unknown error'}`);
      }
    };
    
    loadData();
  }, [fetchCompetitiveAnalytics, showError, dateRange, filters]);
  
  // Chart colors
  const chartColors = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
  ];
  
  // Handlers
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };
  
  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
  };
  
  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange(startDate, endDate);
  };
  
  // Handle export analytics
  const handleExportAnalytics = async (format = 'excel') => {
    try {
      const { startDate, endDate } = dateRange;
      
      // Prepare export params
      const exportParams = {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        format: format,
        // Include filter parameters
        competitorId: filters.competitorId !== 'all' ? filters.competitorId : undefined,
        platform: filters.platform.length > 0 ? filters.platform.join(',') : undefined,
        contentType: filters.contentType !== 'all' ? filters.contentType : undefined,
        metric: filters.metric !== 'all' ? filters.metric : undefined,
        region: filters.region !== 'all' ? filters.region : undefined,
        reportType: 'competitive',
      };
      
      // Call the export service
      await exportAnalyticsData(exportParams);
    } catch (error) {
      showError('Failed to export analytics data: ' + (error.message || 'Unknown error'));
      throw error; // Rethrow to be caught by ExportButton
    }
  };
  
  // Check if data is available
  const hasData = !isLoading && competitiveAnalytics;
  
  // Loading state
  if (isLoading && !competitiveAnalytics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }
  
  // Error state
  if (criticalError && !competitiveAnalytics) {
    return (
      <Box sx={{ py: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            onClick={() => navigate('/analytics')} 
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
          >
            Analytics
          </Link>
          <Typography color="text.primary">Competitive Analysis</Typography>
        </Breadcrumbs>
        
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity="error" 
            onClose={() => clearError()}
            action={
              <Button 
                color="inherit" 
                size="small"
                onClick={() => {
                  clearError();
                  fetchCompetitiveAnalytics();
                }}
              >
                Retry
              </Button>
            }
          >
            {criticalError}
          </Alert>
        </Box>
      </Box>
    );
  }
  
  // Mock data for development - replace with real data from API
  const competitors = [
    { id: 'me', name: 'Our Brand', followers: 35000, engagement: 4.5, posts: 120, sentiment: 85 },
    { id: 'competitor1', name: 'Global Media Group', followers: 42000, engagement: 3.8, posts: 180, sentiment: 72 },
    { id: 'competitor2', name: 'Digital Solutions Inc', followers: 28000, engagement: 5.2, posts: 90, sentiment: 80 },
    { id: 'competitor3', name: 'Creative Agency Co', followers: 22000, engagement: 4.7, posts: 110, sentiment: 78 },
    { id: 'competitor4', name: 'Modern Marketing Ltd', followers: 38000, engagement: 3.2, posts: 150, sentiment: 68 },
  ];
  
  const followersGrowthData = [
    { month: 'Jan', us: 32000, competitor1: 38000, competitor2: 25000, competitor3: 20000, competitor4: 35000 },
    { month: 'Feb', us: 33500, competitor1: 39000, competitor2: 26000, competitor3: 20800, competitor4: 36000 },
    { month: 'Mar', us: 34200, competitor1: 40000, competitor2: 26500, competitor3: 21200, competitor4: 36800 },
    { month: 'Apr', us: 34800, competitor1: 41200, competitor2: 27000, competitor3: 21500, competitor4: 37200 },
    { month: 'May', us: 35000, competitor1: 42000, competitor2: 28000, competitor3: 22000, competitor4: 38000 },
  ];
  
  const engagementRateData = [
    { month: 'Jan', us: 4.1, competitor1: 3.5, competitor2: 4.8, competitor3: 4.3, competitor4: 2.9 },
    { month: 'Feb', us: 4.2, competitor1: 3.6, competitor2: 4.9, competitor3: 4.4, competitor4: 3.0 },
    { month: 'Mar', us: 4.3, competitor1: 3.7, competitor2: 5.0, competitor3: 4.5, competitor4: 3.1 },
    { month: 'Apr', us: 4.4, competitor1: 3.8, competitor2: 5.1, competitor3: 4.6, competitor4: 3.2 },
    { month: 'May', us: 4.5, competitor1: 3.8, competitor2: 5.2, competitor3: 4.7, competitor4: 3.2 },
  ];
  
  const competitiveRadarData = [
    { metric: 'Followers', us: 8.3, competitor1: 10, competitor2: 6.7, competitor3: 5.2, competitor4: 9.0 },
    { metric: 'Engagement', us: 8.7, competitor1: 7.3, competitor2: 10, competitor3: 9.0, competitor4: 6.2 },
    { metric: 'Content Quality', us: 9.2, competitor1: 7.8, competitor2: 8.5, competitor3: 8.0, competitor4: 7.0 },
    { metric: 'Post Frequency', us: 6.7, competitor1: 10, competitor2: 5.0, competitor3: 6.1, competitor4: 8.3 },
    { metric: 'Sentiment', us: 9.5, competitor1: 8.0, competitor2: 8.9, competitor3: 8.7, competitor4: 7.6 },
    { metric: 'Growth Rate', us: 7.5, competitor1: 6.8, competitor2: 7.1, competitor3: 5.8, competitor4: 5.2 },
  ];
  
  const shareOfVoiceData = [
    { name: 'Our Brand', value: 22 },
    { name: 'Global Media Group', value: 28 },
    { name: 'Digital Solutions Inc', value: 18 },
    { name: 'Creative Agency Co', value: 12 },
    { name: 'Modern Marketing Ltd', value: 15 },
    { name: 'Others', value: 5 },
  ];
  
  const topPerformingContent = [
    { competitor: 'Digital Solutions Inc', platform: 'Instagram', type: 'Reel', engagement: 12500, engagementRate: 8.2, date: '2023-01-15' },
    { competitor: 'Our Brand', platform: 'Instagram', type: 'Carousel', engagement: 9800, engagementRate: 7.5, date: '2023-01-22' },
    { competitor: 'Global Media Group', platform: 'TikTok', type: 'Video', engagement: 8900, engagementRate: 5.2, date: '2023-01-18' },
    { competitor: 'Creative Agency Co', platform: 'LinkedIn', type: 'Article', engagement: 7200, engagementRate: 6.8, date: '2023-01-25' },
    { competitor: 'Our Brand', platform: 'Twitter', type: 'Thread', engagement: 6800, engagementRate: 6.2, date: '2023-01-20' },
    { competitor: 'Modern Marketing Ltd', platform: 'Facebook', type: 'Post', engagement: 5400, engagementRate: 4.1, date: '2023-01-12' },
    { competitor: 'Global Media Group', platform: 'Instagram', type: 'Photo', engagement: 5200, engagementRate: 3.8, date: '2023-01-10' },
    { competitor: 'Digital Solutions Inc', platform: 'Twitter', type: 'Poll', engagement: 4800, engagementRate: 5.5, date: '2023-01-05' },
  ];
  
  return (
    <Box sx={{ py: 3 }}>
      {/* Page header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            onClick={() => navigate('/analytics')} 
            sx={{ cursor: 'pointer', textDecoration: 'none' }}
          >
            Analytics
          </Link>
          <Typography color="text.primary">Competitive Analysis</Typography>
        </Breadcrumbs>
        
        <Box sx={getPageHeaderStyles(theme)}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Competitive Analysis
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <AnalyticsFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              dateRange={dateRange}
              onDateRangeChange={handleDateRangeChange}
              filterOptions={filterOptions}
              showDateRange={true}
            />
            
            <ExportButton
              onExport={handleExportAnalytics}
              formats={['excel', 'pdf', 'csv']}
              buttonText="Export Report"
              size="small"
            />
          </Box>
        </Box>
      </Box>
      
      {/* Error message */}
      {criticalError && (
        <Alert 
          severity="error" 
          onClose={() => clearError()}
          sx={{ mb: 4 }}
        >
          {criticalError}
        </Alert>
      )}
      
      {/* Overview KPIs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" mb={2}>
              Competitive Landscape Overview
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Competitor</TableCell>
                    <TableCell align="right">Followers</TableCell>
                    <TableCell align="right">Engagement Rate</TableCell>
                    <TableCell align="right">Content Count</TableCell>
                    <TableCell align="right">Sentiment Score</TableCell>
                    <TableCell>Comparison</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {competitors.map((competitor) => {
                    const isUs = competitor.id === 'me';
                    return (
                      <TableRow 
                        key={competitor.id}
                        sx={{ 
                          bgcolor: isUs ? alpha(theme.palette.primary.main, 0.05) : 'inherit',
                          '&:last-child td, &:last-child th': { border: 0 }
                        }}
                      >
                        <TableCell component="th" scope="row">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar 
                              sx={{ 
                                width: 28, 
                                height: 28, 
                                bgcolor: isUs ? theme.palette.primary.main : theme.palette.secondary.main 
                              }}
                            >
                              {competitor.name.charAt(0)}
                            </Avatar>
                            <Typography 
                              variant="body2" 
                              fontWeight={isUs ? 'bold' : 'normal'}
                            >
                              {competitor.name} {isUs && '(You)'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {competitor.followers.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          {competitor.engagement}%
                        </TableCell>
                        <TableCell align="right">
                          {competitor.posts}
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                            {competitor.sentiment}%
                            <Chip
                              size="small"
                              label={competitor.sentiment >= 80 ? 'High' : competitor.sentiment >= 70 ? 'Medium' : 'Low'}
                              color={competitor.sentiment >= 80 ? 'success' : competitor.sentiment >= 70 ? 'warning' : 'error'}
                              sx={{ height: 20, '& .MuiChip-label': { px: 1, py: 0 } }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          {!isUs && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <MetricComparison
                                currentValue={35000}
                                targetValue={competitor.followers}
                                isPercentage={false}
                                showValue={false}
                                showIcon={true}
                                reverse={true}
                                height={4}
                              />
                              <MetricComparison
                                currentValue={4.5}
                                targetValue={competitor.engagement}
                                isPercentage={true}
                                showValue={false}
                                showIcon={true}
                                reverse={false}
                                height={4}
                              />
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>
      
      {/* Content tabs */}
      <Box sx={getTabsContainerStyles(theme)}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<MetricsIcon />} iconPosition="start" label="Key Metrics" />
          <Tab icon={<ShareIcon />} iconPosition="start" label="Share of Voice" />
          <Tab icon={<TrendIcon />} iconPosition="start" label="Content Analysis" />
          <Tab icon={<AudienceIcon />} iconPosition="start" label="Audience Comparison" />
        </Tabs>
      </Box>
      
      {/* Key Metrics Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <ChartCard
              title="Followers Comparison"
              subtitle="Growth over time"
              icon={<AudienceIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <LineChart
                  data={followersGrowthData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="month" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="us" 
                    name="Our Brand" 
                    stroke={theme.palette.primary.main} 
                    strokeWidth={3}
                    dot={{ stroke: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="competitor1" 
                    name="Global Media Group" 
                    stroke={theme.palette.secondary.main} 
                    strokeWidth={2} 
                    dot={{ stroke: theme.palette.secondary.main, strokeWidth: 2, r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="competitor2" 
                    name="Digital Solutions Inc" 
                    stroke={theme.palette.success.main} 
                    strokeWidth={2} 
                    dot={{ stroke: theme.palette.success.main, strokeWidth: 2, r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="competitor3" 
                    name="Creative Agency Co" 
                    stroke={theme.palette.warning.main} 
                    strokeWidth={2} 
                    dot={{ stroke: theme.palette.warning.main, strokeWidth: 2, r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="competitor4" 
                    name="Modern Marketing Ltd" 
                    stroke={theme.palette.info.main} 
                    strokeWidth={2} 
                    dot={{ stroke: theme.palette.info.main, strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} lg={6}>
            <ChartCard
              title="Engagement Rate Comparison"
              subtitle="Performance by month"
              icon={<EngagementIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <LineChart
                  data={engagementRateData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="month" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`${value}%`, '']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="us" 
                    name="Our Brand" 
                    stroke={theme.palette.primary.main} 
                    strokeWidth={3}
                    dot={{ stroke: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="competitor1" 
                    name="Global Media Group" 
                    stroke={theme.palette.secondary.main} 
                    strokeWidth={2} 
                    dot={{ stroke: theme.palette.secondary.main, strokeWidth: 2, r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="competitor2" 
                    name="Digital Solutions Inc" 
                    stroke={theme.palette.success.main} 
                    strokeWidth={2} 
                    dot={{ stroke: theme.palette.success.main, strokeWidth: 2, r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="competitor3" 
                    name="Creative Agency Co" 
                    stroke={theme.palette.warning.main} 
                    strokeWidth={2} 
                    dot={{ stroke: theme.palette.warning.main, strokeWidth: 2, r: 4 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="competitor4" 
                    name="Modern Marketing Ltd" 
                    stroke={theme.palette.info.main} 
                    strokeWidth={2} 
                    dot={{ stroke: theme.palette.info.main, strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12}>
            <ChartCard
              title="Competitive Analysis Radar"
              subtitle="Performance across key metrics"
              icon={<CompareIcon />}
              isLoading={isLoading}
              height={500}
              magical
            >
              <ResponsiveContainer width="100%" height={450}>
                <RadarChart cx="50%" cy="50%" outerRadius="80%" data={competitiveRadarData}>
                  <PolarGrid stroke={alpha(theme.palette.divider, 0.3)} />
                  <PolarAngleAxis dataKey="metric" tick={{ fill: theme.palette.text.secondary }} />
                  <PolarRadiusAxis angle={30} domain={[0, 10]} tick={{ fill: theme.palette.text.secondary }} />
                  <Radar 
                    name="Our Brand" 
                    dataKey="us" 
                    stroke={theme.palette.primary.main} 
                    fill={alpha(theme.palette.primary.main, 0.2)} 
                    fillOpacity={0.6} 
                  />
                  <Radar 
                    name="Global Media Group" 
                    dataKey="competitor1" 
                    stroke={theme.palette.secondary.main} 
                    fill={alpha(theme.palette.secondary.main, 0.2)} 
                    fillOpacity={0.6} 
                  />
                  <Radar 
                    name="Digital Solutions Inc" 
                    dataKey="competitor2" 
                    stroke={theme.palette.success.main} 
                    fill={alpha(theme.palette.success.main, 0.2)} 
                    fillOpacity={0.6} 
                  />
                  <Radar 
                    name="Creative Agency Co" 
                    dataKey="competitor3" 
                    stroke={theme.palette.warning.main} 
                    fill={alpha(theme.palette.warning.main, 0.2)} 
                    fillOpacity={0.6} 
                  />
                  <Radar 
                    name="Modern Marketing Ltd" 
                    dataKey="competitor4" 
                    stroke={theme.palette.info.main} 
                    fill={alpha(theme.palette.info.main, 0.2)} 
                    fillOpacity={0.6} 
                  />
                  <Legend />
                  <RechartsTooltip contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }} />
                </RadarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Share of Voice Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ChartCard
              title="Share of Voice"
              subtitle="Market presence comparison"
              icon={<ShareIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Pie
                    data={shareOfVoiceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={(entry) => `${entry.name}: ${entry.value}%`}
                    labelLine={false}
                  >
                    {shareOfVoiceData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.name === 'Our Brand' ? theme.palette.primary.main : chartColors[(index % chartColors.length)]} 
                      />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value, name) => [`${value}%`, name]}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ChartCard
              title="Mentions by Platform"
              subtitle="Share of voice across platforms"
              icon={<GlobeIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={[
                    { platform: 'Instagram', us: 35, competitor1: 40, competitor2: 25, competitor3: 15, competitor4: 30 },
                    { platform: 'Facebook', us: 25, competitor1: 35, competitor2: 15, competitor3: 20, competitor4: 25 },
                    { platform: 'Twitter', us: 30, competitor1: 20, competitor2: 35, competitor3: 10, competitor4: 15 },
                    { platform: 'LinkedIn', us: 15, competitor1: 25, competitor2: 10, competitor3: 30, competitor4: 10 },
                    { platform: 'TikTok', us: 20, competitor1: 10, competitor2: 30, competitor3: 5, competitor4: 20 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="platform" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`${value}%`, '']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar dataKey="us" name="Our Brand" fill={theme.palette.primary.main} />
                  <Bar dataKey="competitor1" name="Global Media Group" fill={theme.palette.secondary.main} />
                  <Bar dataKey="competitor2" name="Digital Solutions Inc" fill={theme.palette.success.main} />
                  <Bar dataKey="competitor3" name="Creative Agency Co" fill={theme.palette.warning.main} />
                  <Bar dataKey="competitor4" name="Modern Marketing Ltd" fill={theme.palette.info.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12}>
            <ChartCard
              title="Sentiment Analysis"
              subtitle="Audience sentiment comparison"
              icon={<StarIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart
                  data={[
                    { month: 'Jan', us: 82, competitor1: 70, competitor2: 78, competitor3: 75, competitor4: 65, posts: 25 },
                    { month: 'Feb', us: 83, competitor1: 71, competitor2: 79, competitor3: 76, competitor4: 66, posts: 30 },
                    { month: 'Mar', us: 84, competitor1: 72, competitor2: 80, competitor3: 77, competitor4: 67, posts: 28 },
                    { month: 'Apr', us: 84, competitor1: 72, competitor2: 79, competitor3: 78, competitor4: 68, posts: 32 },
                    { month: 'May', us: 85, competitor1: 72, competitor2: 80, competitor3: 78, competitor4: 68, posts: 35 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="month" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="left" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value, name) => [name === 'posts' ? value : `${value}%`, name]}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Line 
                    yAxisId="left" 
                    type="monotone" 
                    dataKey="us" 
                    name="Our Brand" 
                    stroke={theme.palette.primary.main} 
                    strokeWidth={3}
                  />
                  <Line 
                    yAxisId="left" 
                    type="monotone" 
                    dataKey="competitor1" 
                    name="Global Media Group" 
                    stroke={theme.palette.secondary.main} 
                    strokeWidth={2}
                  />
                  <Line 
                    yAxisId="left" 
                    type="monotone" 
                    dataKey="competitor2" 
                    name="Digital Solutions Inc" 
                    stroke={theme.palette.success.main} 
                    strokeWidth={2}
                  />
                  <Line 
                    yAxisId="left" 
                    type="monotone" 
                    dataKey="competitor3" 
                    name="Creative Agency Co" 
                    stroke={theme.palette.warning.main} 
                    strokeWidth={2}
                  />
                  <Line 
                    yAxisId="left" 
                    type="monotone" 
                    dataKey="competitor4" 
                    name="Modern Marketing Ltd" 
                    stroke={theme.palette.info.main} 
                    strokeWidth={2}
                  />
                  <Bar 
                    yAxisId="right" 
                    dataKey="posts" 
                    name="Post Volume" 
                    fill={alpha(theme.palette.primary.main, 0.2)} 
                    barSize={20} 
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Content Analysis Tab */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ChartCard
              title="Content Distribution by Type"
              subtitle="Content type comparison"
              icon={<TrendIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={[
                    { type: 'Photos', us: 45, competitor1: 40, competitor2: 30, competitor3: 50, competitor4: 55 },
                    { type: 'Videos', us: 25, competitor1: 30, competitor2: 40, competitor3: 20, competitor4: 25 },
                    { type: 'Carousels', us: 20, competitor1: 10, competitor2: 15, competitor3: 20, competitor4: 10 },
                    { type: 'Text', us: 10, competitor1: 20, competitor2: 15, competitor3: 10, competitor4: 10 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="type" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`${value}%`, '']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar dataKey="us" name="Our Brand" fill={theme.palette.primary.main} />
                  <Bar dataKey="competitor1" name="Global Media Group" fill={theme.palette.secondary.main} />
                  <Bar dataKey="competitor2" name="Digital Solutions Inc" fill={theme.palette.success.main} />
                  <Bar dataKey="competitor3" name="Creative Agency Co" fill={theme.palette.warning.main} />
                  <Bar dataKey="competitor4" name="Modern Marketing Ltd" fill={theme.palette.info.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ChartCard
              title="Posting Frequency"
              subtitle="Weekly posts comparison"
              icon={<TrendIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <LineChart
                  data={[
                    { week: 'Week 1', us: 5, competitor1: 8, competitor2: 4, competitor3: 5, competitor4: 7 },
                    { week: 'Week 2', us: 6, competitor1: 7, competitor2: 3, competitor3: 6, competitor4: 8 },
                    { week: 'Week 3', us: 7, competitor1: 9, competitor2: 5, competitor3: 4, competitor4: 6 },
                    { week: 'Week 4', us: 6, competitor1: 8, competitor2: 4, competitor3: 5, competitor4: 7 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="week" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`${value} posts`, '']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="us" 
                    name="Our Brand" 
                    stroke={theme.palette.primary.main} 
                    strokeWidth={3}
                    dot={{ stroke: theme.palette.primary.main, strokeWidth: 2, r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="competitor1" 
                    name="Global Media Group" 
                    stroke={theme.palette.secondary.main} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="competitor2" 
                    name="Digital Solutions Inc" 
                    stroke={theme.palette.success.main} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="competitor3" 
                    name="Creative Agency Co" 
                    stroke={theme.palette.warning.main} 
                  />
                  <Line 
                    type="monotone" 
                    dataKey="competitor4" 
                    name="Modern Marketing Ltd" 
                    stroke={theme.palette.info.main} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12}>
            <ChartCard
              title="Top Performing Content Across Brands"
              subtitle="Highest engagement content"
              icon={<StarIcon />}
              isLoading={isLoading}
              height={500}
              magical
            >
              <TableContainer sx={{ maxHeight: 440 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Competitor</TableCell>
                      <TableCell>Platform</TableCell>
                      <TableCell>Content Type</TableCell>
                      <TableCell align="right">Engagement</TableCell>
                      <TableCell align="right">Engagement Rate</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Comparison</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topPerformingContent.map((content, index) => (
                      <TableRow 
                        key={index} 
                        hover
                        sx={{ 
                          bgcolor: content.competitor === 'Our Brand' ? alpha(theme.palette.primary.main, 0.05) : 'inherit',
                        }}
                      >
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            fontWeight={content.competitor === 'Our Brand' ? 'bold' : 'normal'}
                          >
                            {content.competitor}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={content.platform} 
                            size="small"
                            color={
                              content.platform === 'Instagram' ? 'secondary' :
                              content.platform === 'Facebook' ? 'primary' :
                              content.platform === 'Twitter' ? 'info' :
                              content.platform === 'LinkedIn' ? 'success' : 'warning'
                            }
                            variant="outlined"
                            sx={{ height: 20, '& .MuiChip-label': { px: 1, py: 0 } }}
                          />
                        </TableCell>
                        <TableCell>{content.type}</TableCell>
                        <TableCell align="right">{content.engagement.toLocaleString()}</TableCell>
                        <TableCell align="right">{content.engagementRate}%</TableCell>
                        <TableCell>{content.date}</TableCell>
                        <TableCell align="right">
                          {content.competitor !== 'Our Brand' && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Chip
                                size="small"
                                label={content.engagementRate > 7.5 ? 'Above Ours' : 'Below Ours'}
                                color={content.engagementRate > 7.5 ? 'warning' : 'success'}
                                icon={content.engagementRate > 7.5 ? <TrendingUpIcon fontSize="small" /> : <TrendingDownIcon fontSize="small" />}
                                sx={{ height: 20, '& .MuiChip-label': { px: 1, py: 0 } }}
                              />
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
      
      {/* Audience Comparison Tab */}
      {tabValue === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <ChartCard
              title="Audience Demographics"
              subtitle="Age distribution comparison"
              icon={<AudienceIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={[
                    { age: '18-24', us: 25, competitor1: 20, competitor2: 30, competitor3: 15, competitor4: 35 },
                    { age: '25-34', us: 35, competitor1: 30, competitor2: 25, competitor3: 40, competitor4: 30 },
                    { age: '35-44', us: 25, competitor1: 20, competitor2: 20, competitor3: 25, competitor4: 15 },
                    { age: '45-54', us: 10, competitor1: 15, competitor2: 15, competitor3: 15, competitor4: 10 },
                    { age: '55+', us: 5, competitor1: 15, competitor2: 10, competitor3: 5, competitor4: 10 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="age" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`${value}%`, '']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar dataKey="us" name="Our Brand" fill={theme.palette.primary.main} />
                  <Bar dataKey="competitor1" name="Global Media Group" fill={theme.palette.secondary.main} />
                  <Bar dataKey="competitor2" name="Digital Solutions Inc" fill={theme.palette.success.main} />
                  <Bar dataKey="competitor3" name="Creative Agency Co" fill={theme.palette.warning.main} />
                  <Bar dataKey="competitor4" name="Modern Marketing Ltd" fill={theme.palette.info.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <ChartCard
              title="Audience Gender Distribution"
              subtitle="Gender comparison"
              icon={<AudienceIcon />}
              isLoading={isLoading}
              height={400}
              color="secondary"
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={[
                    { brand: 'Our Brand', male: 45, female: 52, other: 3 },
                    { brand: 'Global Media Group', male: 55, female: 42, other: 3 },
                    { brand: 'Digital Solutions Inc', male: 60, female: 38, other: 2 },
                    { brand: 'Creative Agency Co', male: 40, female: 58, other: 2 },
                    { brand: 'Modern Marketing Ltd', male: 45, female: 50, other: 5 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis type="number" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis dataKey="brand" type="category" tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`${value}%`, '']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar dataKey="male" name="Male" stackId="a" fill={theme.palette.primary.main} />
                  <Bar dataKey="female" name="Female" stackId="a" fill={theme.palette.secondary.main} />
                  <Bar dataKey="other" name="Other" stackId="a" fill={theme.palette.info.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
          
          <Grid item xs={12}>
            <ChartCard
              title="Audience Location Comparison"
              subtitle="Geographic distribution"
              icon={<GlobeIcon />}
              isLoading={isLoading}
              height={400}
              magical
            >
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={[
                    { region: 'North America', us: 45, competitor1: 50, competitor2: 40, competitor3: 35, competitor4: 55 },
                    { region: 'Europe', us: 25, competitor1: 20, competitor2: 30, competitor3: 30, competitor4: 20 },
                    { region: 'Asia', us: 15, competitor1: 10, competitor2: 20, competitor3: 25, competitor4: 15 },
                    { region: 'Middle East', us: 10, competitor1: 5, competitor2: 5, competitor3: 5, competitor4: 5 },
                    { region: 'Latin America', us: 3, competitor1: 10, competitor2: 3, competitor3: 3, competitor4: 3 },
                    { region: 'Africa', us: 2, competitor1: 5, competitor2: 2, competitor3: 2, competitor4: 2 },
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                  <XAxis dataKey="region" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <RechartsTooltip 
                    formatter={(value) => [`${value}%`, '']}
                    contentStyle={{ backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}
                  />
                  <Legend />
                  <Bar dataKey="us" name="Our Brand" fill={theme.palette.primary.main} />
                  <Bar dataKey="competitor1" name="Global Media Group" fill={theme.palette.secondary.main} />
                  <Bar dataKey="competitor2" name="Digital Solutions Inc" fill={theme.palette.success.main} />
                  <Bar dataKey="competitor3" name="Creative Agency Co" fill={theme.palette.warning.main} />
                  <Bar dataKey="competitor4" name="Modern Marketing Ltd" fill={theme.palette.info.main} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default CompetitiveAnalytics;